import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { QrCode, Package, CheckCircle2, XCircle, LogOut, Menu, Camera, AlertTriangle, List, Truck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import LanguageSelector from '../../components/LanguageSelector';
import InventoryManager from '../../components/InventoryManager';

const PorterDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('scanner');
  const [scanMode, setScanMode] = useState('loading'); // 'loading' or 'unloading'
  const [scannedCode, setScannedCode] = useState('');
  const [scannedItems, setScannedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showItemDetails, setShowItemDetails] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const inputRef = useRef(null);
  
  // Job selection for inventory view
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(false);

  useEffect(() => {
    fetchTodaysJobs();
  }, []);

  const fetchTodaysJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await api.get('/jobs');
      // Filter to today's jobs or jobs in progress
      const todayJobs = (response.data || []).filter(job => 
        job.status === 'in_progress' || job.status === 'confirmed'
      );
      setJobs(todayJobs);
      if (todayJobs.length > 0 && !selectedJobId) {
        setSelectedJobId(todayJobs[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('movemate_token');
    localStorage.removeItem('movemate_user');
    setUser(null);
    navigate('/mobile-login');
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scannedCode.trim()) return;

    setLoading(true);
    
    try {
      // Get QR code details from backend
      const response = await api.get(`/qr/${scannedCode.trim()}`);
      const qrData = response.data;
      
      if (qrData.inventory_item) {
        // Scan the item
        await api.post(`/qr/${scannedCode}/scan?scan_type=${scanMode}`);
        
        const newItem = {
          code: scannedCode,
          itemName: qrData.inventory_item.item_name,
          category: qrData.inventory_item.category,
          isFragile: qrData.inventory_item.is_fragile,
          time: new Date().toLocaleTimeString(),
          scanType: scanMode,
          status: 'success'
        };
        
        setScannedItems([newItem, ...scannedItems]);
        setCurrentItem(newItem);
        setShowItemDetails(true);
        toast.success(`${qrData.inventory_item.item_name} scanned for ${scanMode}!`);
      } else {
        // QR code not linked to any item
        const newItem = {
          code: scannedCode,
          itemName: 'Unlinked QR Code',
          category: 'N/A',
          isFragile: false,
          time: new Date().toLocaleTimeString(),
          scanType: scanMode,
          status: 'unlinked'
        };
        
        setScannedItems([newItem, ...scannedItems]);
        toast.warning('QR code not linked to any inventory item');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('QR code not found');
        const newItem = {
          code: scannedCode,
          itemName: 'Unknown',
          time: new Date().toLocaleTimeString(),
          scanType: scanMode,
          status: 'error'
        };
        setScannedItems([newItem, ...scannedItems]);
      } else {
        toast.error('Scan failed');
      }
    } finally {
      setScannedCode('');
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="text-green-600" size={24} />;
      case 'unlinked':
        return <AlertTriangle className="text-yellow-600" size={24} />;
      case 'error':
        return <XCircle className="text-red-600" size={24} />;
      default:
        return <CheckCircle2 className="text-gray-400" size={24} />;
    }
  };

  const todayStats = {
    loaded: scannedItems.filter(i => i.scanType === 'loading' && i.status === 'success').length,
    unloaded: scannedItems.filter(i => i.scanType === 'unloading' && i.status === 'success').length,
    fragile: scannedItems.filter(i => i.isFragile && i.status === 'success').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="porter-dashboard">
      {/* Mobile Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MoveMate" className="h-8" />
              <div>
                <h1 className="text-lg font-bold gradient-text">MoveMate</h1>
                <p className="text-xs text-gray-500">Porter Scanner</p>
              </div>
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2">
              <Menu size={24} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t bg-white">
            <div className="p-4 space-y-2">
              <div className="mb-4">
                <p className="text-sm font-semibold">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-gray-500">Porter</p>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Language</span>
                <LanguageSelector variant="compact" />
              </div>
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-4 pb-20">
        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="scanner" className="flex items-center gap-2" data-testid="tab-scanner">
              <QrCode size={16} />
              Scanner
            </TabsTrigger>
            <TabsTrigger value="inventory" className="flex items-center gap-2" data-testid="tab-inventory">
              <List size={16} />
              Inventory
            </TabsTrigger>
          </TabsList>

          {/* Scanner Tab */}
          <TabsContent value="scanner" className="space-y-4 mt-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{todayStats.loaded}</p>
                  <p className="text-xs text-gray-500">Loaded</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{todayStats.unloaded}</p>
                  <p className="text-xs text-gray-500">Unloaded</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{todayStats.fragile}</p>
                  <p className="text-xs text-gray-500">Fragile</p>
                </CardContent>
              </Card>
            </div>

            {/* Mode Selector */}
            <Card>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={scanMode === 'loading' ? 'default' : 'outline'}
                    className={`h-16 ${scanMode === 'loading' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setScanMode('loading')}
                    data-testid="mode-loading"
                  >
                    <div className="text-center">
                      <Package size={20} className="mx-auto mb-1" />
                      <p className="text-sm font-semibold">Loading</p>
                    </div>
                  </Button>
                  <Button
                    variant={scanMode === 'unloading' ? 'default' : 'outline'}
                    className={`h-16 ${scanMode === 'unloading' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    onClick={() => setScanMode('unloading')}
                    data-testid="mode-unloading"
                  >
                    <div className="text-center">
                      <Package size={20} className="mx-auto mb-1" />
                      <p className="text-sm font-semibold">Unloading</p>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Scanner */}
            <Card className="border-2 border-primary">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <QrCode size={20} className="text-primary" />
                  Scan QR Code
                </CardTitle>
                <CardDescription>
                  Scan items for <span className="font-semibold">{scanMode}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleScan} className="space-y-3">
                  <Input
                    ref={inputRef}
                    placeholder="Scan or enter QR code..."
                    value={scannedCode}
                    onChange={(e) => setScannedCode(e.target.value.toUpperCase())}
                    className="text-xl text-center font-mono tracking-wider h-14"
                    autoFocus
                    disabled={loading}
                    data-testid="scan-input"
                  />
                  <Button 
                    type="submit" 
                    className="w-full h-14 text-lg" 
                    disabled={loading || !scannedCode.trim()}
                    data-testid="scan-btn"
                  >
                    <QrCode size={24} className="mr-2" />
                    {loading ? 'Scanning...' : 'Scan Item'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Scanned Items */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Today's Scans</CardTitle>
                  <Badge variant="outline">{scannedItems.length} items</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {scannedItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package size={48} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No items scanned yet</p>
                    <p className="text-xs mt-1">Scan a QR code to get started</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {scannedItems.map((item, index) => (
                      <div 
                        key={index} 
                        className={`p-3 border rounded-lg flex items-center justify-between ${
                          item.status === 'error' ? 'bg-red-50 border-red-200' :
                          item.status === 'unlinked' ? 'bg-yellow-50 border-yellow-200' :
                          item.isFragile ? 'bg-orange-50 border-orange-200' : ''
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm truncate">{item.itemName || 'Unknown'}</p>
                            {item.isFragile && (
                              <Badge variant="destructive" className="text-xs shrink-0">Fragile</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-xs text-gray-500">{item.code}</code>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">{item.time}</span>
                            <Badge variant="outline" className="text-xs">
                              {item.scanType}
                            </Badge>
                          </div>
                        </div>
                        {getStatusIcon(item.status)}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-4 mt-4">
            {/* Job Selector */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck size={18} />
                  Select Job
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingJobs ? (
                  <p className="text-sm text-gray-500">Loading jobs...</p>
                ) : jobs.length === 0 ? (
                  <p className="text-sm text-gray-500">No active jobs today</p>
                ) : (
                  <Select value={selectedJobId || ''} onValueChange={setSelectedJobId}>
                    <SelectTrigger data-testid="job-selector">
                      <SelectValue placeholder="Select a job..." />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.job_number} - {job.from_address?.city || 'Unknown'} → {job.to_address?.city || 'Unknown'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </CardContent>
            </Card>

            {/* Inventory Manager */}
            {selectedJobId ? (
              <InventoryManager 
                jobId={selectedJobId} 
                mode="porter"
                readOnly={false}
                showVolume={true}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Package size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Select a job to view inventory</p>
                  <p className="text-sm mt-1">Choose a job from the dropdown above</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Item Details Dialog */}
      <Dialog open={showItemDetails} onOpenChange={setShowItemDetails}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="text-green-600" size={24} />
              Item Scanned!
            </DialogTitle>
            <DialogDescription>
              Successfully scanned for {currentItem?.scanType}
            </DialogDescription>
          </DialogHeader>
          
          {currentItem && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg">{currentItem.itemName}</h3>
                <p className="text-sm text-gray-500">{currentItem.category}</p>
                <code className="text-xs text-gray-400 mt-2 block">{currentItem.code}</code>
              </div>
              
              {currentItem.isFragile && (
                <div className="p-3 bg-orange-100 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="text-orange-600" size={20} />
                  <span className="text-sm font-medium text-orange-900">
                    Handle with care - Fragile item
                  </span>
                </div>
              )}
              
              <Button className="w-full" onClick={() => setShowItemDetails(false)}>
                Continue Scanning
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PorterDashboard;
